import { useCallback, useState, type ReactNode } from 'react';
import { message } from 'antd';
import { unpublishWorkflow } from '@/apis/logiblocksAPI';
import useToken from '@/common/helper/useToken';
import CustomDeleteModal from '@/pages/Modals/CustomDeleteModal';

// An unpublish the user has been asked to confirm, held until they answer.
interface PendingUnpublish {
  workflowId: string;
  onUnpublished: () => void;
}

export interface UnpublishConfirmation {
  /**
   * Unpublishes a workflow, asking the user first when runs are in progress.
   * Calls onUnpublished once the workflow really is a draft.
   */
  requestUnpublish: (
    workflowId: string,
    onUnpublished: () => void
  ) => Promise<void>;
  /** The confirmation dialog, for the caller to render. */
  unpublishConfirmModal: ReactNode;
}

const UNPUBLISH_FAILED_MESSAGE =
  'Failed to unpublish workflow. Please try again.';

/**
 * Unpublishes a workflow, first asking the user to accept that runs in progress
 * will be stopped.
 *
 * The caller updates its own copy of the workflow from `onUnpublished` rather
 * than before the call, so the Live/Draft toggle stays where it is while the
 * dialog is open.
 */
export function useUnpublishConfirmation(): UnpublishConfirmation {
  const { getToken } = useToken();
  const [pending, setPending] = useState<PendingUnpublish | null>(null);
  const [isUnpublishing, setIsUnpublishing] = useState(false);

  // Unpublishes straight away when nothing is running. The backend reports that
  // runs are in progress instead of unpublishing, which is what opens the dialog.
  const requestUnpublish = useCallback(
    async (workflowId: string, onUnpublished: () => void) => {
      const token = getToken();
      if (!token) {
        console.error('Cannot unpublish: no authentication token available');
        return;
      }

      try {
        const result = await unpublishWorkflow(token, workflowId);
        if (result.needs_confirmation) {
          setPending({ workflowId, onUnpublished });
          return;
        }
        onUnpublished();
      } catch (error) {
        console.error('Failed to unpublish workflow:', error);
        message.error(UNPUBLISH_FAILED_MESSAGE);
      }
    },
    [getToken]
  );

  // Unpublishes for real, now that the user has accepted losing the runs.
  const confirmUnpublishAndClose = useCallback(async () => {
    const token = getToken();
    if (!pending || !token) {
      return;
    }

    setIsUnpublishing(true);
    try {
      await unpublishWorkflow(token, pending.workflowId, true);
      pending.onUnpublished();
    } catch (error) {
      console.error('Failed to unpublish workflow:', error);
      message.error(UNPUBLISH_FAILED_MESSAGE);
    } finally {
      setIsUnpublishing(false);
      setPending(null);
    }
  }, [getToken, pending]);

  // Backing out leaves the workflow published, which is how the backend left it.
  const dismiss = useCallback(() => {
    setPending(null);
  }, []);

  const unpublishConfirmModal = (
    <CustomDeleteModal
      visible={pending !== null}
      onClose={dismiss}
      onConfirm={() => {
        const _ = confirmUnpublishAndClose();
      }}
      title='In progress run(s) will stop'
      message='Switching off this automation will interrupt the current in progress run(s).'
      cancelText='Cancel'
      confirmText='Turn off'
      confirmColor='var(--danger)'
      disableConfirmBtn={isUnpublishing}
    />
  );

  return { requestUnpublish, unpublishConfirmModal };
}
